import {
    Button,
    ButtonRow,
    Card,
    CheckboxGroup,
    Choicebox,
    Form,
    FormGroup,
    FormSection,
    Select,
    TextInput,
} from '@ksyos/design-system';
import { useState } from 'react';
import type { BaseFundingServicePartMessage } from '@common/validation/fundingServicePart';
import type { FundingServicePart, NewFundingServicePart } from '@frontend/services/api/fundingServicePartsApiService';
import type { ValidationErrors } from '@frontend/utils/validation';
import { ServiceCode } from '@common/enums/serviceCode';
import { ServiceType } from '@common/enums/serviceType';
import { getLanguage } from '@common/translate/translate';
import { baseValidationSchema } from '@common/validation/fundingServicePart';
import { emptyValidationErrors, validate } from '@frontend/utils/validation';
import type { FundableServiceActivity } from './FundingServiceParts';

type FundingServicePartAddEditProps = {
    cancel: () => void;
    part: FundingServicePart | NewFundingServicePart;
    save: (part: FundingServicePart | NewFundingServicePart) => void;
    serviceActivities: FundableServiceActivity[];
};

export function FundingServicePartAddEdit(props: FundingServicePartAddEditProps) {
    const translate = getLanguage();
    const [editPart, setEditPart] = useState({ ...props.part });
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);

    const sortedServiceActivities = [...props.serviceActivities].sort((a, b) => a.name.localeCompare(b.name));

    return (
        <Card>
            <Card.Transition type={'edit'} />
            <Card.Header
                title={
                    translate.finance.fundingServicePart.shortName +
                    ' ' +
                    (Object.hasOwn(props.part, 'id') ? translate.common.edit : translate.common.add).toLowerCase()
                }
            />
            <Card.Body>
                <Form submitOnEnter={false}>
                    <FormSection>
                        <FormGroup
                            hasErrors={errors.has('name')}
                            isRequired={true}
                            label={translate.common.name}
                        >
                            <TextInput
                                name={translate.common.name}
                                onChange={(e) => onChange('name', e.target.value)}
                                value={editPart.name}
                            />
                        </FormGroup>
                        <FormGroup
                            hasErrors={errors.has('fundingForActivityCodes')}
                            isRequired={true}
                            label={translate.finance.fundingServicePart.activities}
                        >
                            <CheckboxGroup
                                layout="row"
                                onChange={(value) => onChange('fundingForActivityCodes', value)}
                                value={editPart.fundingForActivityCodes}
                            >
                                {sortedServiceActivities.map((activity) => (
                                    <Choicebox
                                        key={activity.id}
                                        label={activity.name}
                                        value={activity.code}
                                    />
                                ))}
                            </CheckboxGroup>
                        </FormGroup>
                        <FormGroup
                            hasErrors={errors.has('invoiceServiceCode')}
                            label={translate.finance.fundingServicePart.invoiceServiceName}
                        >
                            <Select
                                onChange={(selected) => onChange('invoiceServiceCode', selected?.value ?? null)}
                                options={[
                                    { label: '-', value: null },
                                    ...(Object.keys(ServiceCode) as ServiceCode[]).map((type) => ({
                                        label: type,
                                        value: type,
                                    })),
                                ]}
                                value={
                                    editPart.invoiceServiceCode
                                        ? {
                                              label: editPart.invoiceServiceCode,
                                              value: editPart.invoiceServiceCode,
                                          }
                                        : { label: '-', value: null }
                                }
                            />
                        </FormGroup>
                        <FormGroup
                            hasErrors={errors.has('invoiceServiceName')}
                            label={translate.finance.fundingServicePart.invoiceServiceName}
                        >
                            <TextInput
                                name={translate.common.name}
                                onChange={(e) => onChange('invoiceServiceName', e.target.value || null)}
                                value={editPart.invoiceServiceName ?? ''}
                            />
                        </FormGroup>
                        <FormGroup
                            hasErrors={errors.has('invoiceServiceType')}
                            label={translate.finance.fundingServicePart.invoiceServiceType}
                        >
                            <Select
                                onChange={(selected) => onChange('invoiceServiceType', selected?.value ?? null)}
                                options={[
                                    { label: '-', value: null },
                                    ...(Object.keys(ServiceType) as ServiceType[]).map((type) => ({
                                        label: translate.serviceTypeEnum[type],
                                        value: type,
                                    })),
                                ]}
                                value={
                                    editPart.invoiceServiceType
                                        ? {
                                              label: translate.serviceTypeEnum[editPart.invoiceServiceType],
                                              value: editPart.invoiceServiceType,
                                          }
                                        : { label: '-', value: null }
                                }
                            />
                        </FormGroup>
                        <ButtonRow
                            justify="right"
                            sx={{ paddingTop: '2.5rem' }}
                        >
                            <Button onClick={props.cancel}>{translate.common.cancel}</Button>
                            <Button onClick={save}>{translate.common.save}</Button>
                        </ButtonRow>
                    </FormSection>
                </Form>
            </Card.Body>
        </Card>
    );

    function save() {
        const {
            errors: validationErrors,
            hasErrors,
            item,
        } = validate<BaseFundingServicePartMessage>({ schema: baseValidationSchema }, editPart);

        setErrors(validationErrors);

        if (hasErrors) {
            return;
        }

        if (Object.hasOwn(editPart, 'id')) {
            props.save({ id: (editPart as FundingServicePart).id, ...item });
        } else {
            props.save(item);
        }
    }

    function onChange<K extends keyof FundingServicePart>(key: K, value: FundingServicePart[K]) {
        setEditPart((prev) => ({ ...prev, [key]: value }));
    }
}
